/**
 * MultiBuy Builder - bundle.js
 * Glue layer between store state and the DOM: renders the sticky footer
 * (hidden until a bundle has at least one item), the in-sheet bundle
 * summary, and reacts to store events. Delegates all click handling for
 * footer/summary controls (view/hide details, add to cart, remove item).
 * Attaches to window.MB.bundleUI
 */
(function (window, document) {
  'use strict';

  var MB = (window.MB = window.MB || {});
  var store = MB.store;
  var discount = MB.discount;
  var utils = MB.utils;
  var api = MB.api;

  // Tracks each bundle's last-painted progress-bar position (0-100) so a
  // fresh render can animate FROM it, even though the footer's innerHTML
  // (and therefore the thumb/fill DOM nodes) is rebuilt from scratch
  // every time - see animateProgress(). renderTierProgress/animateProgress
  // are currently unused by renderFooter (the compact footer design
  // doesn't show a progress bar) but are kept intact and exported from
  // nowhere else needs them - reinstating the bar is a one-line change
  // in renderFooter, not a rewrite.
  var lastProgressPct = {};

  // Tracks which bundles have actually been pushed to the real Shopify
  // cart (via handleCheckout) - lets the same CTA read "Add ... to Cart"
  // before that happens and "Go To Cart" after, without navigating away
  // immediately on success. Staying on the page (rather than redirecting
  // to /cart the instant one bundle is added) is what lets a shopper
  // build/add a curated pack AND Create Your Own Pack in the same visit -
  // an immediate redirect after the first one would cut that short.
  // Cleared when a bundle's items go back to zero (see render()).
  var addedBundles = {};

  // Persists the My Cart popup's per-bundle accordion state across
  // re-renders - renderCartSummarySheet is re-invoked on every
  // bundle:changed (via refreshCustomContent) to keep totals live, so
  // collapse/expand can't just live as a DOM class that render would
  // otherwise stomp.
  var collapsedBundles = {};

  // A real Shopify cart-level discount (e.g. an Automatic Discount that
  // auto-applies once the bundle's products actually land in the real
  // cart) - this app's own "Bundle discount" line below is computed
  // locally from ctx.rules and has no way to know about that, so it's
  // tracked separately from a live /cart.js fetch (see
  // updateCartLevelDiscountCache, called on load and on every
  // cart:updated) and shown as its own line in the Preview Cart totals -
  // see renderTotalsBlock.
  var lastCartLevelDiscount = null;
  function updateCartLevelDiscountCache(cart) {
    var apps = cart && cart.cart_level_discount_applications;
    lastCartLevelDiscount = apps && apps.length ? apps[0] : null;
  }

  function footerEl(bundleId) {
    var scoped = document.querySelector('[data-mb-footer="' + bundleId + '"]');
    if (scoped) return scoped;
    // The global footer is shared across every bundle context on the page;
    // only let the currently-active context claim it so a background
    // context's change (e.g. auto-fill while a different sheet is open)
    // can't hijack what the shopper sees.
    var active = store.getActive();
    if (active && active.id !== bundleId) return null;
    return document.querySelector('[data-mb-footer-global]');
  }

  function hideFooter(el) {
    el.hidden = true;
    el.innerHTML = '';
  }

  // Desktop only: .mb-footer switches to position: sticky; top: 0 once it
  // has content (see multibuy.css), landing it above the curated cards.
  // .mb-toolbar__controls (Create Your Own Pack's search/filter/sort row)
  // is now ALSO sticky at top: 0 - without accounting for the footer,
  // both would try to pin at the exact same spot and overlap once the
  // footer is showing. This measures the footer's current rendered
  // height (0 whenever it's hidden) and feeds it to the toolbar's own
  // sticky offset via a CSS variable, so the toolbar always lands
  // directly below the footer instead of under/over it. Mobile keeps
  // .mb-footer fixed to the bottom of the viewport (unrelated to
  // top-of-page layout), so this is a no-op there.
  var toolbarOffsetResizeBound = false;
  function updateToolbarStickyOffset() {
    if (window.innerWidth < 769) return;
    var footer = document.querySelector('[data-mb-footer-global]');
    var height = footer && !footer.hidden ? footer.getBoundingClientRect().height : 0;
    document.documentElement.style.setProperty('--mb-toolbar-sticky-top', height + 'px');
    if (!toolbarOffsetResizeBound) {
      toolbarOffsetResizeBound = true;
      window.addEventListener('resize', utils.debounce(updateToolbarStickyOffset, 150));
    }
  }

  /**
   * Discount progress bar. Milestone dots/labels are spaced evenly by
   * INDEX - one per discount_rule block, at i/N, 2/N, ... N/N of the
   * track - so they're always evenly distributed regardless of how far
   * apart the merchant's thresholds actually are. The fill/thumb
   * position is a piecewise-linear interpolation across those same
   * evenly-spaced points (0 items -> 0%, rule[0].products_required ->
   * its dot, ..., last rule -> 100%), so it still animates smoothly
   * between milestones and lands exactly on a dot the moment its
   * threshold is reached. Everything is derived from ctx.rules - add,
   * remove, or reorder discount_rule blocks and this reflows with no
   * code changes.
   */
  function renderTierProgress(ctx, totals) {
    var rules = discount.normalizeRules(ctx.rules);
    if (!rules.length) return '';
    var itemCount = totals.itemCount || 0;

    function positionForIndex(i) {
      return ((i + 1) / rules.length) * 100;
    }

    // Piecewise-linear across (0 items, 0%) -> (rule[0], dot 0) -> ... -> (rule[N-1], 100%).
    function thumbPosition() {
      var points = [{ count: 0, pct: 0 }];
      rules.forEach(function (r, i) {
        points.push({ count: r.products_required, pct: positionForIndex(i) });
      });
      var last = points[points.length - 1];
      if (itemCount <= 0) return 0;
      if (itemCount >= last.count) return 100;
      for (var i = 1; i < points.length; i++) {
        if (itemCount <= points[i].count) {
          var prev = points[i - 1];
          var curr = points[i];
          var span = curr.count - prev.count;
          var ratio = span > 0 ? (itemCount - prev.count) / span : 1;
          return prev.pct + ratio * (curr.pct - prev.pct);
        }
      }
      return 100;
    }

    var thumbPct = utils.clamp(thumbPosition(), 0, 100);

    var labelsHtml = rules
      .map(function (r, i) {
        var status = 'upcoming';
        if (totals.tier && totals.tier.products_required === r.products_required) {
          status = 'current';
        } else if (itemCount >= r.products_required) {
          status = 'completed';
        }
        var pos = positionForIndex(i);
        var edge = pos <= 1 ? ' mb-progress__label--start' : pos >= 99 ? ' mb-progress__label--end' : '';
        return (
          '<div class="mb-progress__label mb-progress__label--' + status + edge + '" style="left:' + pos + '%">' +
          '<span class="mb-progress__label-count">' + r.products_required + ' Items</span>' +
          '<span class="mb-progress__label-pct">' + r.discount_percent + '% Off</span>' +
          '</div>'
        );
      })
      .join('');

    var dotsHtml = rules
      .map(function (r, i) {
        var reached = itemCount >= r.products_required;
        return (
          '<span class="mb-progress__dot' + (reached ? ' mb-progress__dot--filled' : '') + '" style="left:' + positionForIndex(i) + '%">' +
          (reached
            ? '<svg class="mb-progress__dot-check" viewBox="0 0 16 16" aria-hidden="true"><path d="M3 8.5l3 3 7-7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
            : '') +
          '</span>'
        );
      })
      .join('');

    return (
      '<div class="mb-progress">' +
      '<div class="mb-progress__labels">' + labelsHtml + '</div>' +
      '<div class="mb-progress__track" data-mb-progress-track data-target-pct="' + thumbPct + '">' +
      '<span class="mb-progress__fill" style="width:' + thumbPct + '%"></span>' +
      dotsHtml +
      '<span class="mb-progress__thumb" style="left:' + thumbPct + '%"></span>' +
      '</div>' +
      '</div>'
    );
  }

  /**
   * The footer's whole innerHTML (and therefore the thumb/fill nodes) is
   * rebuilt on every render, which would otherwise make the CSS
   * transition on .mb-progress__thumb/.mb-progress__fill useless - a
   * freshly-created node has no "previous" position to animate from.
   * This paints the new nodes at the bundle's last known position,
   * forces a reflow so the browser commits that as the starting point,
   * then moves them to the real target on the next frame so the
   * transition actually plays.
   */
  function animateProgress(el, bundleId) {
    var track = utils.qs('[data-mb-progress-track]', el);
    if (!track) return;
    var targetPct = parseFloat(track.dataset.targetPct || '0');
    var fromPct = typeof lastProgressPct[bundleId] === 'number' ? lastProgressPct[bundleId] : 0;
    var thumb = utils.qs('.mb-progress__thumb', track);
    var fill = utils.qs('.mb-progress__fill', track);

    if (fromPct !== targetPct) {
      if (thumb) thumb.style.left = fromPct + '%';
      if (fill) fill.style.width = fromPct + '%';
      track.getBoundingClientRect(); // force layout so the "from" state is committed
      requestAnimationFrame(function () {
        if (thumb) thumb.style.left = targetPct + '%';
        if (fill) fill.style.width = targetPct + '%';
      });
    }

    lastProgressPct[bundleId] = targetPct;
  }

  function renderItemRow(item, bundleId) {
    return (
      '<div class="mb-summary-item" data-key="' + item.key + '">' +
      '<img class="mb-summary-item__img" src="' + utils.escapeHtml(item.productImage || '') + '" alt="" loading="lazy">' +
      '<div class="mb-summary-item__meta">' +
      '<p class="mb-summary-item__title">' + utils.escapeHtml(item.productTitle) + '</p>' +
      '<p class="mb-summary-item__price">' + utils.formatMoney(item.price) +
      (item.variantTitle ? ' &bull; Size ' + utils.escapeHtml(item.variantTitle) : '') +
      ' &bull; Qty ' + item.quantity + '</p>' +
      '</div>' +
      // data-bundle-id here matters: this row can live inside the
      // Preview Cart popup, which is a separate DOM tree from the
      // sticky footer entirely - the click handler's usual fallback
      // (walking up to a [data-mb-footer]/[data-mb-footer-global]
      // ancestor, then to store.getActive()) finds neither, so without
      // this it silently resolved to whichever bundle happened to be
      // "active" instead of the one this specific item actually
      // belongs to - removing an item from any bundle other than the
      // active one then matched no item by key and did nothing.
      '<button type="button" class="mb-summary-item__remove" data-mb-action="remove-item" data-key="' + item.key + '" data-bundle-id="' + bundleId + '" aria-label="Remove product">&times;</button>' +
      '</div>'
    );
  }

  /** Overlapping circular thumbnails from the bundle's own items - no separate asset, reuses each item's productImage. */
  function renderPreviewThumbs(ctx) {
    return (
      '<div class="mb-footer__preview-thumbs">' +
      ctx.items
        .slice(0, 4)
        .map(function (item) {
          return '<img class="mb-footer__preview-thumb" src="' + utils.escapeHtml(item.productImage || '') + '" alt="" loading="lazy">';
        })
        .join('') +
      '</div>'
    );
  }

  /** Reused by both the sheet's cart-summary content and (formerly) the footer - one source of truth for this row. */
  function renderSummaryBar(totals) {
    return (
      '<div class="mb-footer__summary-bar mb-qv-added-banner">' +
      '<span class="mb-footer__summary-count"> <svg class="mb-footer__summary-check" viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="8" fill="#CFD1E5" /><path d="M4.5 8.3l2.2 2.2 4.8-4.8" fill="none" stroke="#3964C7" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>' + totals.itemCount + ' Item' + (totals.itemCount === 1 ? '' : 's') + ' Added</span>' +
      '<span class="mb-footer__summary-total">Total <strong>' + utils.formatMoney(totals.total) + '</strong>' +
      (totals.compareSubtotal > totals.total ? ' <span class="mb-price mb-price--compare">' + utils.formatMoney(totals.compareSubtotal) + '</span>' : '') +
      (totals.savings > 0 ? '<div class="mb-footer__summary-savings"> <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_3659_35442)"><path d="M0.5 5.58595L0.5 2C0.5 1.60217 0.65815 1.22076 0.939455 0.939455C1.22076 0.65815 1.60217 0.5 2 0.5L5.58595 0.5L5.7344 0.507325C6.0777 0.541485 6.4004 0.69335 6.6465 0.939455L10.9995 5.2925L11.0576 5.354C11.3401 5.6678 11.4976 6.0758 11.4976 6.5L11.4956 6.58445C11.4748 7.0062 11.2985 7.4066 10.9995 7.7075L7.7075 10.9995C7.4066 11.2985 7.0062 11.4748 6.58445 11.4956L6.5 11.4976C6.04755 11.4976 5.6134 11.3184 5.2925 10.9995L0.939455 6.6465C0.69335 6.4004 0.541485 6.0777 0.507325 5.7344L0.5 5.58595ZM1.5 5.58595L1.50244 5.63525C1.51382 5.7497 1.56442 5.8574 1.64648 5.93945L5.99755 10.2906L6.0498 10.3374C6.17645 10.4405 6.33525 10.4976 6.5 10.4976L6.5703 10.4942C6.73275 10.4781 6.8856 10.4066 7.00245 10.2906L10.2915 7.00145C10.4069 6.88475 10.4781 6.7323 10.4942 6.5703L10.4976 6.5C10.4976 6.3122 10.4235 6.132 10.2915 5.99855L5.93945 1.64648C5.8574 1.56442 5.7497 1.51382 5.63525 1.50244L5.58595 1.5L2 1.5C1.86739 1.5 1.74025 1.55271 1.64648 1.64648C1.55271 1.74025 1.5 1.86739 1.5 2L1.5 5.58595Z" fill="#598978"/><path d="M3.76172 4.01172C3.89979 4.01172 4.01172 3.89979 4.01172 3.76172C4.01172 3.62365 3.89979 3.51172 3.76172 3.51172C3.62365 3.51172 3.51172 3.62365 3.51172 3.76172C3.51172 3.89979 3.62365 4.01172 3.76172 4.01172Z" fill="#598978"/><path d="M3.76172 3.51172C3.62365 3.51172 3.51172 3.62365 3.51172 3.76172C3.51172 3.89979 3.62365 4.01172 3.76172 4.01172C3.89979 4.01172 4.01172 3.89979 4.01172 3.76172C4.01172 3.62365 3.89979 3.51172 3.76172 3.51172ZM4.51172 3.76172C4.51172 4.17593 4.17593 4.51172 3.76172 4.51172C3.3475 4.51172 3.01172 4.17593 3.01172 3.76172C3.01172 3.3475 3.3475 3.01172 3.76172 3.01172C4.17593 3.01172 4.51172 3.3475 4.51172 3.76172Z" fill="#598978"/></g><defs><clipPath id="clip0_3659_35442"><rect width="12" height="12" fill="white"/></clipPath></defs></svg> You saved ' + utils.formatMoney(totals.savings) + (totals.discountPercent ? ' (' + totals.discountPercent + '%)' : '') + '</div>' : '') +
      '</span>' +
      '</div>'
    );
  }

  /** Subtotal / discount / savings / total breakdown, used inside the My Cart popup. */
  function renderTotalsBlock(totals) {
    return (
      '<div class="mb-footer__totals">' +
      '<div class="mb-footer__line"><span>Subtotal</span><span>' + utils.formatMoney(totals.subtotal) + '</span></div>' +
      (lastCartLevelDiscount
        ? '<div class="mb-footer__line mb-footer__line--coupon"><span>' + utils.escapeHtml(lastCartLevelDiscount.title || 'Coupon Applied') + '</span><span>-' + utils.formatMoney(lastCartLevelDiscount.total_allocated_amount) + '</span></div>'
        : '') +
      (totals.discountAmount > 0
        ? '<div class="mb-footer__line mb-footer__line--discount"><span>Bundle discount (' + totals.discountPercent + '% OFF)</span><span>-' + utils.formatMoney(totals.discountAmount) + '</span></div>'
        : '') +
      '<div class="mb-footer__line mb-footer__line--total"><span>You Pay</span><span>' + utils.formatMoney(totals.total) + '</span></div>' +
      (totals.savings > 0
        ? '<div class="mb-footer__saved-pill"><svg class="mb-footer__saved-pill-icon" viewBox="0 0 16 16" aria-hidden="true"><path d="M8.5 1.5l6 6a1.5 1.5 0 0 1 0 2.1l-4.9 4.9a1.5 1.5 0 0 1-2.1 0l-6-6V2a.5.5 0 0 1 .5-.5h6.5z" fill="none" stroke="currentColor" stroke-width="1.3"/><circle cx="5" cy="5" r="1" fill="currentColor"/></svg>You saved ' + utils.formatMoney(totals.savings) + '</div>'
        : '') +
      '</div>'
    );
  }

  /**
   * Compact collapsed footer: title + "View Details" (opens the My Cart
   * popup - see openCartSummary/renderCartSummarySheet below, which
   * reuses the existing bottom-sheet open/close mechanics rather than
   * expanding the footer in place), then a row of overlapping product
   * thumbnails, live price, and the checkout CTA.
   */
  /**
   * Sums item count/price/savings across every bundle bundlesWithItems()
   * currently counts (already-added curated packs + Create Your Own
   * Pack) - used for the sticky footer's count/price display, so it
   * reads as "everything you're currently building" instead of
   * silently switching to show only whichever bundle was touched last
   * (which used to make an already-added curated pack's count/total
   * disappear from view the moment the shopper started Create Your Own
   * Pack, even though it was still safely in the real cart). discountPercent
   * here is a blended effective rate (discountAmount / subtotal), since a
   * single % doesn't cleanly describe bundles on different tiers.
   */
  /**
   * Combines every bundle with items into ONE discount computation
   * instead of looking up each bundle's own tier against its own item
   * count and summing the results - the latter meant 2 items in a
   * curated pack (its own 2-item tier) plus 3 in Create Your Own Pack
   * (its own 3-item tier) blended two different percentages together,
   * instead of correctly earning the 5-item tier's rate on the whole
   * combined subtotal. Every bundle shares the same section-wide
   * discount_rule blocks, so any one context's rules represent the
   * whole cart's tiers - concatenating every bundle's real items and
   * running the normal computeTotals once gives the correct single
   * tier for the true combined count, with no separate re-derivation of
   * discountPercent/etc needed since computeTotals already returns them.
   */
  function combinedTotals() {
    var withItems = bundlesWithItems();
    if (!withItems.length) {
      return { itemCount: 0, subtotal: 0, compareSubtotal: 0, discountPercent: 0, discountAmount: 0, total: 0, savings: 0, tier: null, nextTier: null, items: [] };
    }
    var mergedItems = [];
    withItems.forEach(function (ctx) {
      mergedItems = mergedItems.concat(ctx.items);
    });
    var totals = discount.computeTotals(mergedItems, withItems[0].rules);
    // Exposes the merged list itself (not just the numbers) so the
    // desktop footer bar's thumbnail preview can show a stable "first 2
    // items across every bundle" instead of switching to whichever
    // bundle happens to be "active" at the moment - see
    // renderDesktopFooterBar.
    totals.items = mergedItems;
    return totals;
  }

  function renderFooter(ctx, totals) {
    var combined = combinedTotals();
    var headerHtml =
      '<div class="mb-footer__header">' +
      '<button type="button" class="mb-link mb-footer__toggle-btn" data-mb-action="toggle-details" aria-expanded="false">' +
      'View Details' +
      '</button>' +
      '</div>';

    // Unlike the View Collection page's own CTA (which stays a two-step
    // "Add ... to Cart" -> click -> "Go To Cart" so a shopper can keep
    // building a second pack afterward), this landing-page sticky footer
    // always reads "Go To Cart" and goes there directly in one click -
    // adding first if it hasn't been added yet, or just navigating if it
    // has (see checkout-and-go/go-to-cart below).
    var added = !!addedBundles[ctx.id];
    var ctaHtml = added
      ? '<button type="button" class="mb-btn mb-btn--primary mb-footer__cta" data-mb-action="go-to-cart" data-bundle-id="' + ctx.id + '">' +
        '<span class="mb-btn__label">Go To Cart</span>' +
        '</button>'
      : '<button type="button" class="mb-btn mb-btn--primary mb-footer__cta" data-mb-action="checkout-and-go" data-bundle-id="' + ctx.id + '">' +
        '<span class="mb-btn__label">Go To Cart</span><span class="mb-btn__spinner" hidden></span>' +
        '</button>';

    var previewRowHtml =
      '<div class="mb-footer__preview-row">' +
          '<div class="mb-footer_price_row">' +
      renderPreviewThumbs(ctx) +
      '<div class="mb-footer__preview-pricing">' +
      '<span class="mb-footer__title">' + combined.itemCount + ' Item' + (combined.itemCount === 1 ? '' : 's') + '</span>' +
      '<div class="mb-footer__preview-price">' +
      '<span class="mb-price">' + utils.formatMoney(combined.total) + '</span>' +
      (combined.compareSubtotal > combined.total ? '<span class="mb-price mb-price--compare">' + utils.formatMoney(combined.compareSubtotal) + '</span>' : '') +
      (combined.discountPercent ? '<span class="mb-pct-save">' + combined.discountPercent + '% save</span>' : '') +
      '</div>' +
      '</div>' +
      '</div>' +
      ctaHtml +
      '</div>';

    // Wrapped with display:contents (see CSS) so this stays exactly what
    // mobile has always rendered/looked like - the wrapper adds no box of
    // its own, it's purely a hook for the desktop media query below to
    // hide it in favor of .mb-footer__desktop-bar.
    var mobileHtml = '<div class="mb-footer__mobile-bar">' + headerHtml + previewRowHtml + '</div>';

    return '<div class="mb-footer__inner">' + mobileHtml + renderDesktopFooterBar(ctx, totals, combined) + '</div>';
  }

  /**
   * Desktop-only sticky bar (hidden on mobile via CSS - see
   * .mb-footer__desktop-bar / .mb-footer__mobile-bar in multibuy.css):
   * thumbnails + "X of Y Items Selected" + next-tier hint + a lightweight
   * progress bar on the left, You Save / Total / View Details on the
   * right. "Y" is the highest discount_rule threshold configured (or the
   * next upcoming one, whichever is still ahead) - fully driven by
   * ctx.rules, same as the milestone progress bar elsewhere.
   */
  function renderDesktopFooterBar(ctx, totals, combined) {
    if (!combined) combined = totals;
    var rules = discount.normalizeRules(ctx.rules);
    if (!rules.length) return '';
    var maxRule = rules[rules.length - 1];
    // Every bundle shares the same section-wide discount_rule tiers, so
    // "target"/the tier hint/the progress % all need to be driven by
    // the SAME combined item count as the title below - mixing
    // combined.itemCount into the title while target/pct/hint stayed
    // keyed to this one bundle's own (usually much smaller) totals.
    // itemCount produced mutually inconsistent numbers (e.g. "5 Items
    // Selected" next to "3 Items away from unlocking 30% OFF" and a 25%
    // progress bar that didn't agree with either).
    var target = combined.nextTier ? combined.nextTier.products_required : maxRule.products_required;
    var pct = utils.clamp(target > 0 ? (combined.itemCount / target) * 100 : 0, 0, 100);

    // Combined across every bundle with items, not just the active
    // one's own ctx.items - otherwise switching "active" (e.g. picking a
    // product in Create Your Own Pack right after building a curated
    // pack) swapped in a completely different pair of photos, which
    // read as the thumbnails randomly changing. This keeps the same
    // first two photos in place as more items are added anywhere;
    // only the "+N" count grows.
    var combinedItems = combined.items || ctx.items;
    var shownThumbs = combinedItems.slice(0, 2);
    var moreCount = combined.itemCount - shownThumbs.length;
    var thumbsHtml =
      '<div class="mb-footer__desktop-thumbs">' +
      shownThumbs
        .map(function (item) {
          return '<img class="mb-footer__desktop-thumb" src="' + utils.escapeHtml(item.productImage || '') + '" alt="" loading="lazy">';
        })
        .join('') +
      (moreCount > 0 ? '<span class="mb-footer__desktop-thumb-more">+' + moreCount + '</span>' : '') +
      '</div>';

    var hintHtml = '';
    if (combined.nextTier) {
      var remaining = combined.nextTier.products_required - combined.itemCount;
      hintHtml =
        '<p class="mb-footer__desktop-hint">' + remaining + ' Item' + (remaining === 1 ? '' : 's') +
        ' away from unlocking <strong>' + combined.nextTier.discount_percent + '% OFF</strong></p>';
    } else if (combined.tier) {
      hintHtml = '<p class="mb-footer__desktop-hint">Maximum discount of <strong>' + combined.tier.discount_percent + '% OFF</strong> unlocked</p>';
    }

    // Combined across every bundle with items (curated packs already
    // added + Create Your Own Pack), same as the mobile bar's title -
    // otherwise switching from a filled curated pack to Create Your Own
    // Pack made this look like it had "reset" back down to just the
    // newly-active bundle's own count. Once that combined count exceeds
    // the tier target above, "X of Y" stops making sense (Y is already
    // cleared), so it drops to a plain count instead.
    var displayCount = combined.itemCount;
    var titleText = displayCount > target
      ? displayCount + ' Item' + (displayCount === 1 ? '' : 's') + ' Selected'
      : displayCount + ' of ' + target + ' Item' + (target === 1 ? '' : 's') + ' Selected';

    return (
      '<div class="mb-footer__desktop-bar">' +
      '<div class="mb-footer__desktop-left">' +
      thumbsHtml +
      '<div class="mb-footer__desktop-progress">' +
      '<p class="mb-footer__desktop-title">' + titleText + '</p>' +
      hintHtml +
      '<div class="mb-progress-lite">' +
      '<span class="mb-progress-lite__track"><span class="mb-progress-lite__fill" style="width:' + pct + '%"></span></span>' +
      '<span class="mb-progress-lite__pct">' + Math.round(pct) + '% Completed</span>' +
      '</div>' +
      '</div>' +
      '</div>' +
      '<div class="mb-footer__desktop-right">' +
      (combined.savings > 0
        ? '<div class="mb-footer__desktop-stat mb-footer__desktop-stat--save"><span>You Save</span><strong>' + utils.formatMoney(combined.savings) + '</strong></div>'
        : '') +
      '<div class="mb-footer__desktop-stat"><span>Total</span><strong>' + utils.formatMoney(combined.total) + '</strong></div>' +
      '<button type="button" class="mb-btn mb-btn--primary mb-footer__desktop-cta" data-mb-action="toggle-details">' +
      'View Details' +
      '<svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M6 3.5L10.5 8L6 12.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
      '</button>' +
      '</div>' +
      '</div>'
    );
  }


  function bundlesWithItems() {
    return store.getAllContexts().filter(function (ctx) {
      if (!ctx.items.length) return false;
      // Same intent-gating as the sticky footer (see render()): a
      // curated bundle auto-fills the instant View Collection opens,
      // just to pre-build a pack for review - glancing at it and coming
      // back shouldn't make it show up in My Cart (or count toward
      // checkout-all) as if it were something the shopper actually
      // committed to. Custom (Create Your Own Pack) has no auto-fill -
      // every item there is a deliberate click - so it's included as
      // soon as it has items, same as before.
      if (ctx.type === 'curated' && !addedBundles[ctx.id]) return false;
      return true;
    });
  }

  /**
   * "My Cart" popup content, rendered into the shared bottom sheet via
   * MB.collection.openWithContent. Shows every bundle that currently has
   * items - a curated pack AND Create Your Own Pack both at once - each
   * as its own accordion (reusing renderSummaryBar/renderItemRow), not
   * just whichever bundle's footer happened to open the popup. Per-bundle
   * totals are summed into one grand total for renderTotalsBlock/the CTA.
   */
  function renderCartSummarySheet() {
    var allCtx = bundlesWithItems();
    if (!allCtx.length) return '';

    var bundleBlocksHtml = allCtx
      .map(function (ctx) {
        // Per-bundle accordion still shows this bundle's own standalone
        // totals (its own tier against just its own items) - only the
        // grand total below (combinedTotals) needs to reflect the
        // correct single tier across every bundle's combined items.
        var totals = discount.computeTotals(ctx.items, ctx.rules);
        var itemsHtml = ctx.items.map(function (item) { return renderItemRow(item, ctx.id); }).join('');
        var collapsed = !!collapsedBundles[ctx.id];

        return (
          '<div class="mb-cart-summary__bundle' + (collapsed ? ' mb-cart-summary__bundle--collapsed' : '') + '">' +
          '<button type="button" class="mb-cart-summary__bundle-header" data-mb-action="toggle-bundle-accordion" data-bundle-id="' + ctx.id + '" aria-expanded="' + !collapsed + '">' +
          '<span class="mb-cart-summary__bundle-name">' +
          utils.escapeHtml(ctx.title || 'Bundle') +
          '</span>' +
          '<svg class="mb-cart-summary__chevron" viewBox="0 0 16 16" aria-hidden="true"><path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
          '</button>' +
          '<div class="mb-cart-summary__bundle-body">' +
          renderSummaryBar(totals) +
          '<div class="mb-footer__items">' + itemsHtml + '</div>' +
          '</div>' +
          '</div>'
        );
      })
      .join('');

    var grand = combinedTotals();

    return (
      '<div class="mb-sheet__header mb-cart-summary__header">' +
      '<div><h3 class="mb-sheet__title">Preview Cart</h3>' +
      '<p class="mb-cart-summary__subtitle">' + allCtx.length + ' Bundle' + (allCtx.length === 1 ? '' : 's') +
      ' &middot; ' + grand.itemCount + ' Item' + (grand.itemCount === 1 ? '' : 's') + '</p></div>' +
      '<button type="button" class="mb-link mb-footer__toggle-btn" data-mb-action="sheet-close" aria-expanded="true"><span class="mb-link__dk">&times;</span><span class="mb-link__mb">Hide Details</span></button>' +
      '</div>' +
      '<div class="mb-sheet__body">' +
      bundleBlocksHtml +
      '</div>' +
      '<div class="mb-sheet__cta">' +
      renderTotalsBlock(grand) +
      '<button type="button" class="mb-btn mb-btn--primary mb-btn--block view-multi" data-mb-action="checkout-all">' +
      '<span class="mb-btn__label">Go To Cart</span><span class="mb-btn__spinner" hidden></span>' +
      '</button>' +
      '</div>'
    );
  }

  function openCartSummary() {
    var withItems = bundlesWithItems();
    if (!withItems.length) return;
    // Every bundle starts expanded each time the popup is freshly
    // opened, rather than carrying over whatever was collapsed from a
    // previous viewing - collapsedBundles is module-level state that
    // would otherwise persist for the rest of the page session (e.g. a
    // bundle collapsed once would stay collapsed on every reopen).
    // Collapsing still works normally while this viewing is open (see
    // toggle-bundle-accordion), only the moment of opening resets it.
    collapsedBundles = {};
    // openWithContent only needs *a* valid bundleId for its own shell
    // bookkeeping (sheet.bundleId/contentMode) - renderCartSummarySheet
    // itself ignores it and always aggregates every bundle with items,
    // so prefer the active one but fall back to any bundle that has
    // items rather than failing if nothing is "active" yet.
    var active = store.getActive();
    var bundleId = (active && active.items.length ? active.id : withItems[0].id);
    MB.collection.openWithContent(bundleId, function () {
      return renderCartSummarySheet();
    });
  }

  /** Bottom CTA of the (possibly multi-bundle) My Cart popup: adds every
   * not-yet-added bundle that has items, then goes to the real cart -
   * one click covers a curated pack AND Create Your Own Pack together. */
  function handleCheckoutAll(btn) {
    var label = btn.querySelector('.mb-btn__label');
    var spinner = btn.querySelector('.mb-btn__spinner');
    var pending = bundlesWithItems().filter(function (ctx) {
      return !addedBundles[ctx.id];
    });

    if (!pending.length) {
      goToCart();
      return;
    }

    btn.disabled = true;
    if (spinner) spinner.hidden = false;
    if (label) label.textContent = 'Adding...';

    Promise.all(
      pending.map(function (ctx) {
        return MB.cart.addBundleToCart(ctx.id).then(function () {
          addedBundles[ctx.id] = true;
        });
      })
    )
      .then(goToCart)
      .catch(function (err) {
        btn.disabled = false;
        if (MB.collection.refreshCustomContent) MB.collection.refreshCustomContent();
        utils.dispatch('multibuy:toast', { message: err.message || 'Could not add to cart', error: true });
      });
  }

  function render(ctx) {
    var totals = discount.computeTotals(ctx.items, ctx.rules);

    if (totals.itemCount === 0) {
      delete lastProgressPct[ctx.id];
      delete addedBundles[ctx.id];
    }

    // footerEl(ctx.id) is null whenever ctx isn't the "active" bundle -
    // the shared sticky footer only ever shows one bundle at a time. But
    // the in-sheet summary mirrors and the (possibly multi-bundle) My
    // Cart popup are independent of which bundle is "active", so they
    // must keep updating even when this ctx doesn't own the footer right
    // now - returning early here used to skip them entirely, which is
    // why removing the last item of a bundle sitting in the background
    // (relative to "active") never closed/refreshed an open My Cart
    // popup showing it.
    var el = footerEl(ctx.id);
    if (el) {
      // Curated bundles auto-fill the instant View Collection opens, just
      // to pre-build a pack for the shopper to review/tweak - that's not
      // an expression of intent to buy, so the sticky footer (which lives
      // on the storefront page, outside View Collection) shouldn't pop up
      // just from having opened (and maybe closed) it. It only appears
      // once the bundle is actually pushed to the real cart. Create Your
      // Own Pack has no auto-fill - every item there is a deliberate click
      // - so it keeps showing live, unaffected.
      var suppressForCurated = ctx.type === 'curated' && !addedBundles[ctx.id];

      if (totals.itemCount === 0 || suppressForCurated) {
        hideFooter(el);
      } else {
        el.dataset.bundleId = ctx.id;
        el.hidden = false;
        el.innerHTML = renderFooter(ctx, totals);
      }
      updateToolbarStickyOffset();
    }

    // Keep any in-sheet summary mirrors in sync.
    utils.qsa('[data-mb-sheet-summary="' + ctx.id + '"]').forEach(function (sumEl) {
      sumEl.innerHTML = renderSheetSummary(ctx, totals);
    });

    // If the My Cart popup is open and this change just emptied out the
    // last bundle it had anything to show, close it outright instead of
    // re-rendering it down to blank (empty-but-open reads as a glitch,
    // not as "there's nothing left").
    if (MB.collection.isShowingCustomContent && MB.collection.isShowingCustomContent() && !bundlesWithItems().length) {
      MB.collection.close();
    } else if (MB.collection.refreshCustomContent) {
      MB.collection.refreshCustomContent();
    }

    utils.dispatch('multibuy:footer:rendered', { bundleId: ctx.id, totals: totals });
  }

  /**
   * Desktop-only "Bundle Items / Total / Saved Amount" 3-stat block for
   * the View Collection page's footer (see the reference design) - hidden
   * on mobile via CSS (multibuy.css), which keeps showing the existing
   * renderSummaryBar row instead (also unchanged there, and still the
   * only thing the My Cart popup's per-bundle accordion uses - this block
   * is local to renderSheetSummary, not renderSummaryBar itself, so it
   * has no effect on My Cart).
   */
  function renderDesktopSheetStats(ctx, totals,ctaHtml) {
    var target = ctx.minSlots || totals.itemCount;
    return (
      '<div class="mb-sheet-stats">' +
      '<div class="mb-sheet-stats__item">' +
      '<span class="mb-sheet-stats__icon"><svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M4.5 8.3l2.2 2.2 4.8-4.8" stroke="#496FEF" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg></span>' +
      '<span class="mb-sheet-stats__text"><span class="mb-sheet-stats__label">Bundle Items</span><span class="mb-sheet-stats__value">' + totals.itemCount + '/' + target + ' Added</span></span>' +
      '</div>' +
      '<div class="mb-sheet-stats__item">' +
      '<span class="mb-sheet-stats__icon"><svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_3738_9717)"><path d="M15.2725 8.06711V5.81833C15.2725 5.0558 14.681 4.43566 13.9337 4.37553L11.8454 0.727861C11.6518 0.390503 11.3393 0.149382 10.9654 0.0492583C10.5932 -0.0501832 10.2041 0.00204321 9.87096 0.195915L2.71189 4.3638H1.45453C0.652353 4.3638 0 5.01612 0 5.81833V14.5455C0 15.3476 0.652319 16 1.45453 16H13.818C14.6202 16 15.2725 15.3477 15.2725 14.5455V12.2967C15.6949 12.1461 15.9998 11.7462 15.9998 11.2728V9.09101C15.9998 8.61756 15.6949 8.21772 15.2725 8.06711ZM13.0887 4.3638H9.91927L12.2964 2.97987L13.0887 4.3638ZM11.935 2.34875L8.47387 4.3638H7.03823L11.5761 1.7218L11.935 2.34875ZM10.2371 0.824439C10.4015 0.728202 10.5936 0.702634 10.7772 0.751656C10.9629 0.80136 11.1178 0.921392 11.214 1.08935L11.2147 1.09068L5.5929 4.3638H4.15732L10.2371 0.824439ZM14.5452 14.5455C14.5452 14.9464 14.2189 15.2727 13.818 15.2727H1.45453C1.05363 15.2727 0.727284 14.9464 0.727284 14.5455V5.81833C0.727284 5.41743 1.05363 5.09108 1.45453 5.09108H13.818C14.2189 5.09108 14.5452 5.41743 14.5452 5.81833V8.00012H12.3634C11.1603 8.00012 10.1817 8.97878 10.1817 10.1819C10.1817 11.385 11.1603 12.3637 12.3634 12.3637H14.5452V14.5455ZM15.2725 11.2728C15.2725 11.4734 15.1095 11.6364 14.9089 11.6364H12.3634C11.5613 11.6364 10.9089 10.9841 10.9089 10.1819C10.9089 9.37972 11.5612 8.72737 12.3634 8.72737H14.9089C15.1095 8.72737 15.2725 8.89035 15.2725 9.09101V11.2728Z" fill="#3964C7"/><path d="M12.364 9.45312C11.9631 9.45312 11.6367 9.77947 11.6367 10.1804C11.6367 10.5813 11.9631 10.9076 12.364 10.9076C12.7649 10.9076 13.0912 10.5813 13.0912 10.1804C13.0913 9.77947 12.7649 9.45312 12.364 9.45312Z" fill="#3964C7"/></g><defs><clipPath id="clip0_3738_9717"><rect width="16" height="16" fill="white"/></clipPath></defs></svg></span>' +
      '<span class="mb-sheet-stats__text"><span class="mb-sheet-stats__label">Total</span><span class="mb-sheet-stats__value">' + utils.formatMoney(totals.total) +
      (totals.compareSubtotal > totals.total ? ' <span class="mb-price mb-price--compare">' + utils.formatMoney(totals.compareSubtotal) + '</span>' : '') +
      '</span></span>' +
      '</div>' +
      '<div class="mb-sheet-stats__item last-item">' +
      '<span class="mb-sheet-stats__icon"><svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M2 2h5.4a1 1 0 0 1 .7.3l5.6 5.6a1 1 0 0 1 0 1.4l-4.4 4.4a1 1 0 0 1-1.4 0L2.3 8.1a1 1 0 0 1-.3-.7V2z" stroke="#51856C" stroke-width="1.3" stroke-linejoin="round"/><circle cx="4.7" cy="4.7" r="0.9" fill="#496FEF"/></svg></span>' +
      '<span class="mb-sheet-stats__text"><span class="mb-sheet-stats__label">Saved Amount</span><span class="mb-sheet-stats__value">' + utils.formatMoney(totals.savings) +
      (totals.discountPercent ? ' (' + totals.discountPercent + '%)' : '') +
      '</span></span>' +
      '</div>' +
      ctaHtml +
      '</div>'
    );
  }

  /**
   * View Collection's footer: the same checkmark/count/total/savings bar
   * used by the My Cart popup (renderSummaryBar - one source of truth for
   * that row), plus a real "Add to Cart" CTA. Reuses handleCheckout via
   * the existing checkout-bundle action - no new add-to-cart path.
   */
  function renderSheetSummary(ctx, totals) {
    if (!totals) totals = discount.computeTotals(ctx.items, ctx.rules);
    var ctaHtml = addedBundles[ctx.id]
      ? '<button type="button" class="mb-btn mb-btn--primary mb-btn--block view-multi" data-mb-action="go-to-cart" data-bundle-id="' + ctx.id + '">' +
        '<span class="mb-btn__label">Go To Cart</span>' +
        '</button>'
      : '<button type="button" class="mb-btn mb-btn--primary mb-btn--block" data-mb-action="checkout-bundle" data-bundle-id="' + ctx.id + '">' +
        '<span class="mb-btn__label">Add Multi-Buy Pack to Cart (' + totals.itemCount + ')</span><span class="mb-btn__spinner" hidden></span>' +
        '</button>';
    return renderSummaryBar(totals) + renderDesktopSheetStats(ctx, totals,ctaHtml) ;
  }

  function bindFooterEvents() {
    // Picks up any discount already live on the real cart from a
    // previous visit/page load, then keeps it current as items are
    // added - addBundleToCart (cart.js) dispatches cart:updated with the
    // freshly fetched cart right after a successful add. Only the
    // already-open Preview Cart popup needs an explicit refresh here;
    // every other surface (footer, sheet) doesn't render this line.
    api.getCart().then(updateCartLevelDiscountCache);
    document.addEventListener('cart:updated', function (e) {
      updateCartLevelDiscountCache(e.detail && e.detail.cart);
      if (MB.collection.refreshCustomContent) MB.collection.refreshCustomContent();
    });

    document.addEventListener('click', function (e) {
      var actionEl = e.target.closest('[data-mb-action]');
      if (!actionEl) return;
      var action = actionEl.dataset.mbAction;

      // Doesn't target a specific bundle - covers every bundle with
      // items at once, so it shouldn't be gated behind resolving one.
      if (action === 'checkout-all') {
        handleCheckoutAll(actionEl);
        return;
      }

      var footer = actionEl.closest('[data-mb-footer], [data-mb-footer-global]');
      var bundleId = actionEl.dataset.bundleId || (footer && footer.dataset.bundleId) || (store.getActive() && store.getActive().id);
      if (!bundleId) return;
      var ctx = store.getContext(bundleId);
      if (!ctx) return;

      switch (action) {
        case 'toggle-details':
          openCartSummary();
          break;
        case 'remove-item':
          store.removeItem(bundleId, actionEl.dataset.key);
          break;
        case 'view-details':
          utils.dispatch('multibuy:sheet:open', { bundleId: bundleId });
          break;
        case 'toggle-bundle-accordion':
          collapsedBundles[bundleId] = !collapsedBundles[bundleId];
          if (MB.collection.refreshCustomContent) MB.collection.refreshCustomContent(bundleId);
          break;
        case 'checkout-bundle':
          handleCheckout(bundleId, actionEl);
          break;
        case 'checkout-and-go':
          handleCheckoutAndGo(bundleId, actionEl);
          break;
        case 'go-to-cart':
          goToCart();
          break;
        default:
          break;
      }
    });

    store.on('bundle:changed', render);
    store.on('context:created', render);
    store.on('active:changed', function (ctx) {
      if (ctx) render(ctx);
    });
  }

  function goToCart() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    window.location.href = root.replace(/\/$/, '') + '/cart';
  }

  /**
   * Adds the bundle to the real Shopify cart, then stays on the page and
   * relabels the CTA to "Go To Cart" instead of redirecting immediately -
   * an instant redirect would force-end the session the moment ONE
   * bundle is added, making it impossible to also add a second (e.g. a
   * curated pack, then Create Your Own Pack) without navigating back.
   * ctx.items is intentionally left alone (not cleared) so the
   * footer/sheet summary keep showing what was just added instead of
   * disappearing.
   */
  function handleCheckout(bundleId, btn) {
    var label = btn.querySelector('.mb-btn__label');
    var spinner = btn.querySelector('.mb-btn__spinner');
    btn.disabled = true;
    if (spinner) spinner.hidden = false;
    if (label) label.textContent = 'Adding...';

    MB.cart
      .addBundleToCart(bundleId)
      .then(function () {
        addedBundles[bundleId] = true;
        var ctx = store.getContext(bundleId);
        if (ctx) render(ctx);
      })
      .catch(function (err) {
        // Re-render rather than hand-resetting the label/spinner: this
        // button's original text differs between the footer ("Add to
        // Cart") and the View Collection page ("Add Multi-Buy Pack to
        // Cart (N)"), and a fresh render regenerates whichever is
        // correct for where this button lives.
        btn.disabled = false;
        var ctx = store.getContext(bundleId);
        if (ctx) render(ctx);
        utils.dispatch('multibuy:toast', { message: err.message || 'Could not add bundle to cart', error: true });
      });
  }

  /**
   * Landing-page sticky footer only: adds then navigates to /cart in one
   * click, unlike handleCheckout (View Collection's CTA), which stays on
   * the page so a shopper can keep building a second pack. This is the
   * "I'm done, take me to checkout" surface, not a building surface.
   */
  function handleCheckoutAndGo(bundleId, btn) {
    var label = btn.querySelector('.mb-btn__label');
    var spinner = btn.querySelector('.mb-btn__spinner');
    btn.disabled = true;
    if (spinner) spinner.hidden = false;
    if (label) label.textContent = 'Adding...';

    MB.cart
      .addBundleToCart(bundleId)
      .then(function () {
        addedBundles[bundleId] = true;
        goToCart();
      })
      .catch(function (err) {
        btn.disabled = false;
        var ctx = store.getContext(bundleId);
        if (ctx) render(ctx);
        utils.dispatch('multibuy:toast', { message: err.message || 'Could not add bundle to cart', error: true });
      });
  }

  MB.bundleUI = {
    render: render,
    renderSheetSummary: renderSheetSummary,
    bindFooterEvents: bindFooterEvents
  };
})(window, document);
